import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  EretailDynamicFormComponent,
} from '../../../shared/components/eretail-dynamic-form/eretail-dynamic-form.component';
import { ButtonComponent } from "../../../shared/components/button/eretail-button/button.component";
import { EretailContentWrapperHeaderComponent } from '../../../shared/components/eretail-content-wrapper/eretail-content-wrapper-header/eretail-content-wrapper-header.component';
import { EretailModalComponent } from "../../../shared/components/modal/eretail-modal/eretail-modal.component";
import { ReportsService } from '../../../core/services/reports.service';
import { IEretailForm } from 'src/app/shared/interfaces/form';
import { ToastService } from 'src/app/core/helpers/toast.service';
import { months, years } from 'src/app/core/constants/extract-form.data';
import saveAs from 'file-saver';
import { Subscription } from 'rxjs';

@Component({
  selector: 'extract',
  standalone: true,
  imports: [
    EretailDynamicFormComponent,
    EretailContentWrapperHeaderComponent,
    ButtonComponent,
    EretailModalComponent
  ],
  templateUrl: './extract.component.html',
  styleUrl: './extract.component.scss',
})
export class ExtractComponent implements AfterViewInit, OnDestroy {

  @ViewChild('dynamicForm') extractReportFormEl!: EretailDynamicFormComponent;

  readonly reportsService = inject(ReportsService);
  readonly _toast = inject(ToastService);

  protected isLoading = false;

  private subscriptions = new Subscription();

  protected extractForm: IEretailForm = {
    id: 1,
    type: 'non-noded',
    formTitle: '',
    formSubTitle: '',
    fields: [
      {
        elId: 'tipo-relatorio',
        nodeName: 'tipoRelatorio',
        displayName: 'Tipo de Relatório',
        placeholder: 'Selecione o Relatório',
        fill: true,
        fieldType: 'select',
        options: [{ id: 1, value: 'Mapa de vendas' }, { id: 2, value: 'Relatório de Ruptura' }],
        lineOrder: 0,
        validators: [{ type: 'required' }]
      },
      {
        elId: 'mes-inicial',
        displayName: 'Mês Inicial',
        placeholder: 'Selecione o Mês',
        fill: true,
        fieldType: 'select',
        nodeName: 'mesInicio',
        options: months,
        lineOrder: 1,
        validators: [{ type: 'required' }]
      },
      {
        elId: 'ano-inicial',
        displayName: 'Ano Inicial',
        placeholder: 'Selecione o Ano',
        fill: true,
        fieldType: 'select',
        nodeName: 'anoInicio',
        lineOrder: 1,
        options: years,
        validators: [{ type: 'required' }]
      },
      {
        elId: 'mes-final',
        displayName: 'Mês Final',
        placeholder: 'Selecione o Mês',
        fill: true,
        fieldType: 'select',
        nodeName: 'mesFim',
        lineOrder: 2,
        options: months,
        validators: [{ type: 'required' }]
      },
      {
        elId: 'ano-final',
        displayName: 'Ano Final',
        placeholder: 'Selecione o Ano',
        fill: true,
        fieldType: 'select',
        nodeName: 'anoFim',
        options: years,
        lineOrder: 2,
        validators: [{ type: 'required' }]
      }
    ],
    lines: [
      {
        order: 0
      },
      {
        order: 1
      },
      {
        order: 2
      }
    ],
    sections: []
  }

  ngAfterViewInit(): void {
    this.changeMonthListBasedOnYear();

  }

  protected onSubmit(): void {
    this.generateReport();

  }

  // Limita a lista de meses até o mês atual caso escolha o ano atual (ano início ou fim). 
  private changeMonthListBasedOnYear(): void {
    const currentYear = new Date().getFullYear(); 
    const currentMonth = new Date().getMonth() + 1; 

    const initYearSubs = this.extractReportFormEl.getControl('anoInicio')?.valueChanges.subscribe(selectedYear => {
      if (selectedYear === currentYear) {
        const monthsUntilCurrent = months.filter(month => month.id <= currentMonth);

        this.extractReportFormEl.editField('mesInicio', { selectValues: monthsUntilCurrent });

      } else if (selectedYear < currentYear) {
        this.extractReportFormEl.editField('mesInicio', { selectValues: months });

      }

    });

    const endYearSubs = this.extractReportFormEl.getControl('anoFim')?.valueChanges.subscribe(selectedYear => {
      if (selectedYear === currentYear) {
        const monthsUntilCurrent = months.filter(month => month.id <= currentMonth);

        this.extractReportFormEl.editField('mesFim', { selectValues: monthsUntilCurrent });

      } else if (selectedYear < currentYear) {
        this.extractReportFormEl.editField('mesFim', { selectValues: months });

      }

    });

    this.subscriptions.add(initYearSubs);
    this.subscriptions.add(endYearSubs);

  }

  private generateReport(): void {
    this.isLoading = true;
    const extractReportFormEl = this.extractReportFormEl.dynamicForm;
    const formData = extractReportFormEl.getRawValue();
    const isDateValid = this.checkDateValidity(formData);

    if (extractReportFormEl.invalid) {
      this.extractReportFormEl.dynamicForm.markAllAsTouched();
      this.isLoading = false;
      return;

    }

    if (!isDateValid) {
      this.isLoading = false;
      return;

    }

    if (formData.tipoRelatorio === 2) {
      this.reportsService.getRupturaReport()
        .subscribe({
          next: (blob) => {
            saveAs(blob, 'RelatorioRuptura.xlsx');
            this._toast.showSuccessToast('Relatório de Ruptura baixado com sucesso!', '');
            this.isLoading = false;

          },
          error: (response) => {
            this._toast.showErrorToast('Falha ao gerar Relatório de Ruptura.', response?.error[0]);
            this.isLoading = false;

          }
        });
    } else {
      // Outros relatórios
      this.reportsService.export(formData)
        .subscribe({
          next: () => {
            this._toast.showSuccessToast(
              'Relatório Gerado com Sucesso!',
              'Você receberá um e-mail assim que o arquivo estiver pronto.'
            );

            this.isLoading = false;

          },
          error: (response) => {
            this._toast.showErrorToast('Falha ao gerar relatório.', response?.error[0]);
            this.isLoading = false;

          }

        });
    }
  }
  
  private checkDateValidity(formValue: {
    anoFim: number;
    anoInicio: number;
    mesFim: number;
    mesInicio: number;
    tipoRelatorio: number;
  }): boolean {
    /** 
    * meses em JS começam de 0, somamos 1 para alinhar como nossa lista de meses;
    */
    const currentMonth = new Date().getMonth() + 1; 
    const currentYear = new Date().getFullYear(); 
  
    const isEndYearLessThanInital = formValue.anoFim < formValue.anoInicio; 
    const isPeriodSameYear = formValue.anoFim === formValue.anoInicio; 
    const isEndMonthLessThanInital = formValue.mesFim < formValue.mesInicio;
    
    const isEndYearCurrentAndEndMonthGreaterThanCurrent = formValue.anoFim === currentYear && formValue.mesFim > currentMonth;
    const isStartYearCurrentAndStartMonthGreaterThanCurrent = formValue.anoInicio === currentYear && formValue.mesInicio > currentMonth;
  
    if (isEndYearLessThanInital) {
      this._toast.showWarningToast('Período Inválido', 'O ano Final não pode ser anterior ao Inicial.');
      return false;

    } else if (isPeriodSameYear && isEndMonthLessThanInital) {
      this._toast.showWarningToast('Período Inválido', 'O mês Final não pode ser anterior ao Inicial.');
      return false;
      
    } else if (isEndYearCurrentAndEndMonthGreaterThanCurrent) {
      this._toast.showWarningToast('Período Inválido', 'O mês Final não pode ser maior que o mês atual.');
      return false;

    } else if (isStartYearCurrentAndStartMonthGreaterThanCurrent) {
      this._toast.showWarningToast('Período Inválido', 'O mês Inicial não pode ser maior que o mês atual.');
      return false;

    }
  
    return true;
    
  }
 
  ngOnDestroy() {
    this.subscriptions.unsubscribe();

  }
}
